import styled from 'styled-components'
import { media } from '../../styles/media'

/**
 * The chrome both warehouse designers wear: a full-bleed canvas with a floating
 * toolbar on top, a selection panel at the bottom, and a hint in the corner.
 *
 * It lived twice, byte for byte, in `WarehouseLayoutDesigner` and
 * `WarehouseSiteDesigner` — the second file's own comment said it "mirrors" the
 * first. Two copies of a responsive bar is two chances for the phone breakpoints
 * to drift apart, which is exactly what a user notices when one screen turns
 * into icons and its neighbour does not.
 */

export const Wrap = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background: ${p => p.theme.area.bg};
    user-select: none;
`

export const Toolbar = styled.div`
    position: absolute;
    top: 0.75rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 0.5rem;
    align-items: center;
    z-index: 10;
    background: ${p => p.theme.card.background};
    padding: 0.4rem 0.75rem;
    border-radius: 8px;
    box-shadow: ${p => p.theme.card.boxShadow};
    border: 1px solid ${p => p.theme.area.lightBorder};

    /* Centring a fixed-width bar assumes it fits. On a phone it does not, so it
       spans the width instead and scrolls if the icons still overflow. */
    ${media.phone} {
        top: 0.5rem;
        left: 0.4rem;
        right: 0.4rem;
        transform: none;
        gap: 0.25rem;
        padding: 0.3rem 0.4rem;
        overflow-x: auto;
        scrollbar-width: none;
        &::-webkit-scrollbar { display: none; }
    }
`

export const Btn = styled.button`
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 0.35rem;
    padding: 0.3rem 0.75rem;
    border-radius: 6px;
    border: 1px solid ${p => p.$active ? p.theme.btn.primary : p.theme.area.lightBorder};
    background: ${p => p.$active ? p.theme.btn.primary : p.theme.card.background};
    color: ${p => p.$active ? p.theme.btn.font : p.theme.font.color};
    font-size: 0.8rem;
    cursor: pointer;
    white-space: nowrap;
    &:hover { opacity: 0.85; }
    &:disabled { opacity: 0.45; cursor: default; }
`

// A button on the floating toolbar. Only these go icon-only on a phone — the
// selection panel's buttons are plain `Btn` and stay words, because that panel
// holds one or two of them and has the room.
const BarBtn = styled(Btn)`
    ${media.phone} {
        padding: 0.45rem;
        min-width: 2.1rem;
        justify-content: center;
        font-size: 1.05rem;
    }
`

// A toolbar button carries both its word and its icon, and CSS picks one. The
// alternative — choosing in JS off a breakpoint — needs a resize listener to
// stay right, and would let the two forms drift apart. `title`/`aria-label`
// carry the word in either form, so the icon is never the only name it has.
const BtnText = styled.span`
    ${media.phone} { display: none; }
`

const BtnIcon = styled.span`
    display: none;
    ${media.phone} { display: inline-flex; align-items: center; }
`

// For a control that must not become an icon on a crowded phone toolbar —
// today, Reset, which re-places every hall and rack and deletes all paths.
const DesktopBtn = styled(BarBtn)`
    ${media.phone} { display: none; }
`

/**
 * A toolbar button.
 *
 * `label` is always the accessible name. `text` overrides only what is shown,
 * so a transient busy state cannot rename the button for a screen reader.
 * `desktopOnly` keeps it off the phone bar entirely.
 */
export function ToolBtn({ icon, label, text, desktopOnly, ...rest }) {
    const Base = desktopOnly ? DesktopBtn : BarBtn
    return (
        <Base title={label} aria-label={label} {...rest}>
            <BtnIcon>{icon}</BtnIcon>
            <BtnText>{text ?? label}</BtnText>
        </Base>
    )
}

export const ZoomControl = styled.div`
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    border: 1px solid ${p => p.theme.area.lightBorder};
    border-radius: 6px;
    overflow: hidden;
`

export const ZoomBtn = styled.button`
    padding: 0.3rem 0.6rem;
    background: ${p => p.theme.card.background};
    border: none;
    color: ${p => p.theme.font.color};
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    &:hover { background: ${p => p.theme.card.bg2}; }

    ${media.phone} { padding: 0.45rem 0.4rem; }
`

export const ZoomLabel = styled.span`
    padding: 0.3rem 0.5rem;
    min-width: 3.5rem;
    text-align: center;
    font-size: 0.8rem;
    color: ${p => p.theme.font.label};
    border-left: 1px solid ${p => p.theme.area.lightBorder};
    border-right: 1px solid ${p => p.theme.area.lightBorder};
    background: ${p => p.theme.card.background};

    /* The widest reading is four characters. 3.5rem reserves room for more
       than that, which is a whole icon's worth of a phone toolbar. */
    ${media.phone} {
        min-width: 2.5rem;
        padding: 0.3rem 0.1rem;
        font-size: 0.7rem;
    }
`

export const SelectionPanel = styled.div`
    position: absolute;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 0.75rem;
    align-items: center;
    z-index: 10;
    background: ${p => p.theme.card.background};
    padding: 0.5rem 0.9rem;
    border-radius: 10px;
    box-shadow: ${p => p.theme.card.boxShadow};
    border: 1px solid ${p => p.theme.area.lightBorder};
    font-size: 0.85rem;
    color: ${p => p.theme.font.color};

    /* Centring assumes it fits; a long warehouse name on a phone does not. */
    ${media.phone} {
        left: 0.4rem;
        right: 0.4rem;
        bottom: 0.6rem;
        transform: none;
        flex-wrap: wrap;
        gap: 0.5rem;
    }
`

export const PanelLabel = styled.span`
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    & small { color: ${p => p.theme.form.readOnly}; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; }
    & b { font-weight: 600; }
`

export const DangerBtn = styled(Btn)`
    border-color: #ef4444;
    color: #ef4444;
    &:hover { background: #ef4444; color: #fff; opacity: 1; }
`

export const Hint = styled.div`
    position: absolute;
    bottom: 0.6rem;
    right: 0.85rem;
    font-size: 0.72rem;
    color: ${p => p.theme.form.readOnly};
    pointer-events: none;

    /* The toolbar already crowds a phone; a hint about Ctrl+click does not
       belong on a screen with no Ctrl key. */
    ${media.phone} { display: none; }
`
